import { Table } from '@mantine/core';

import { NOT_INDICATED, type LabelValueRow } from './digestTemplate';
import classes from './RegistryDigestView.module.css';

/** One shaded label per row, the value beside it: Registration Details
 * and a document's key facts. */
export function LabelValueTable({
  rows,
  label,
}: Readonly<{ rows: readonly LabelValueRow[]; label: string }>) {
  return (
    <div className={classes.scroll}>
      <Table withTableBorder withColumnBorders verticalSpacing={4} fz="sm" aria-label={label}>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.label}>
              <Table.Th scope="row" className={`${classes.shaded} ${classes.label}`}>
                {row.label}
              </Table.Th>
              <Table.Td className={row.provided ? classes.value : classes.missing}>
                {row.value}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
}

/** A cell that may hold the "not indicated" placeholder. */
function Cell({ value, className }: Readonly<{ value: string; className?: string }>) {
  const tone = value === NOT_INDICATED ? classes.missing : classes.value;
  return <Table.Td className={className ? `${tone} ${className}` : tone}>{value}</Table.Td>;
}

/**
 * A table under a shaded header row: Management, Ownership / Shareholders
 * and the financial figures, the same shape as the copy for Word's table
 * block. `rowHeaders` shades each row's first cell as its label (the
 * metric); `numeric` right-aligns the other cells (printed figures).
 */
export function GridTable({
  columns,
  rows,
  label,
  rowHeaders = false,
  numeric = false,
}: Readonly<{
  columns: readonly string[];
  rows: readonly string[][];
  label: string;
  rowHeaders?: boolean;
  numeric?: boolean;
}>) {
  return (
    <div className={classes.scroll}>
      <Table withTableBorder withColumnBorders verticalSpacing={4} fz="sm" aria-label={label}>
        <Table.Thead>
          <Table.Tr>
            {columns.map((column, index) => (
              // Two periods may share a heading ("Period not stated").
              <Table.Th key={`${column}:${index}`} className={classes.shaded}>
                {column}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(([first, ...rest], index) => (
            // Rows carry no id; two officers may even share a name.
            <Table.Tr key={`${first}:${index}`}>
              {rowHeaders ? (
                <Table.Th scope="row" className={`${classes.shaded} ${classes.label}`}>
                  {first}
                </Table.Th>
              ) : (
                <Cell value={first} />
              )}
              {rest.map((value, column) => (
                <Cell
                  key={`${columns[column + 1]}:${column}`}
                  value={value}
                  className={numeric ? classes.figure : undefined}
                />
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
}
